import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Mail, Lock, CreditCard, Bell, Save, Trash2, AlertTriangle, X, Loader2, Settings2, RotateCcw, Clock, Upload, Camera, Phone, Monitor, Download, ListChecks } from 'lucide-react';
import { PEOPLES_PICK_HIDDEN } from '../../utils/featureFlags';
import { useAuth } from '../../context/AuthContext';
import { getDJDefaults, saveDJDefaults, resetDJDefaults, SYSTEM_DEFAULTS, DJDefaultEventSettings } from '../../services/djDefaults';
import { clampQueueDepth } from '../../utils/playerQueueSync';
import { requestPushPermission, removePushToken, isPushEnabled } from '../../services/pushNotifications';
import { doc, getDoc, setDoc, deleteDoc, writeBatch, getDocs, collection, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { deleteEvent } from '../../services/events';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential, deleteUser } from 'firebase/auth';
import toast from 'react-hot-toast';
import { PlayModePicker } from '../../components/dj/PlayModePicker';
import { BRIDGE_LATEST_RELEASE_URL, BRIDGE_MAC_INSTALLER_URL, BRIDGE_WINDOWS_INSTALLER_URL } from '../../config/bridgeDownloads';
import { MIN_PARTICIPANTS, UPVOTE_OVERRIDE_OPTIONS, VOTE_OUT_THRESHOLD_OPTIONS } from '../../utils/voteOut';
import { PollLibraryPanel } from '../../components/polls/PollLibraryPanel';

const SETTINGS_TAB_IDS = ['profile', 'desktopHelper', 'eventDefaults', 'polls', 'security', 'notifications', 'billing'];

function initialSettingsTab(requestedTab: string | null): string {
  return requestedTab && SETTINGS_TAB_IDS.includes(requestedTab) ? requestedTab : 'profile';
}

// Profile picture constraints
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// Session timeout options in minutes
const SESSION_TIMEOUT_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, sessionTimeoutMinutes, setSessionTimeoutMinutes } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => initialSettingsTab(searchParams.get('tab')));
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);

  // Delete account modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Event defaults state
  const [eventDefaults, setEventDefaults] = useState<DJDefaultEventSettings>(SYSTEM_DEFAULTS);

  // Profile picture state
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Default contact info state
  const [defaultContactName, setDefaultContactName] = useState('');
  const [defaultContactPhone, setDefaultContactPhone] = useState('');
  const [defaultContactEmail, setDefaultContactEmail] = useState('');

  // Load event defaults, profile, and notification prefs on mount
  useEffect(() => {
    async function loadSettings() {
      if (user?.uid) {
        // Load DJ defaults from Firestore
        const defaults = await getDJDefaults(user.uid);
        setEventDefaults(defaults);

        // Load user doc for profile pic and notification prefs
        const userRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.profilePic) setProfilePic(data.profilePic);
          if (data.defaultContactName) setDefaultContactName(data.defaultContactName);
          if (data.defaultContactPhone) setDefaultContactPhone(data.defaultContactPhone);
          if (data.defaultContactEmail) setDefaultContactEmail(data.defaultContactEmail);
          if (data.notificationPreferences) {
            setEmailNotifications(data.notificationPreferences.weeklyDigest ?? true);
            // Sync push toggle with actual browser permission + stored token
            setPushNotifications(isPushEnabled());
          }
        }
      }
      if (user?.displayName) {
        setName(user.displayName);
      }
      if (user?.email) {
        setEmail(user.email);
      }
    }
    loadSettings();
  }, [user?.uid, user?.email, user?.displayName]);

  const handleSaveEventDefaults = async () => {
    if (!user?.uid) return;
    await saveDJDefaults(user.uid, eventDefaults);
    toast.success('Default event settings saved');
  };

  const handleResetEventDefaults = async () => {
    if (!user?.uid) return;
    await resetDJDefaults(user.uid);
    setEventDefaults(SYSTEM_DEFAULTS);
    toast.success('Event defaults reset to system defaults');
  };

  // Save notification preferences to Firestore
  const saveNotificationPrefs = async (emailEnabled: boolean, pushEnabled: boolean) => {
    if (!user?.uid) return;
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, {
      notificationPreferences: {
        newRequests: true,
        requestVotes: true,
        eventReminders: true,
        attendeeMessages: true,
        weeklyDigest: emailEnabled,
        soundAlerts: pushEnabled,
      },
    }, { merge: true });
  };

  const handleToggleEmailNotifications = async () => {
    const newValue = !emailNotifications;
    setEmailNotifications(newValue);
    await saveNotificationPrefs(newValue, pushNotifications);
    toast.success(newValue ? 'Email notifications enabled' : 'Email notifications disabled');
  };

  const handleTogglePushNotifications = async () => {
    if (!user?.uid) return;
    const enabling = !pushNotifications;

    if (enabling) {
      const token = await requestPushPermission(user.uid);
      if (!token) {
        toast.error('Push notifications were blocked. Check your browser settings.');
        return;
      }
      setPushNotifications(true);
      await saveNotificationPrefs(emailNotifications, true);
      toast.success('Push notifications enabled');
    } else {
      await removePushToken();
      setPushNotifications(false);
      await saveNotificationPrefs(emailNotifications, false);
      toast.success('Push notifications disabled');
    }
  };

  // Handle profile picture upload
  const handleProfilePicUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setUploadError(null);

    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setUploadError(`Invalid file type. Please upload: JPEG, PNG, GIF, or WebP (max ${MAX_FILE_SIZE / 1024 / 1024}MB)`);
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WebP');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      toast.error(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    setUploadingPhoto(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result as string;
      if (user?.uid) {
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, { profilePic: dataUrl }, { merge: true });
        setProfilePic(dataUrl);
        toast.success('Profile picture updated!');
      }
      setUploadingPhoto(false);
    };
    reader.onerror = () => {
      setUploadError('Failed to read file. Please try again.');
      toast.error('Failed to read file');
      setUploadingPhoto(false);
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const tabs = [
    { id: 'profile', icon: User, label: 'Profile' },
    { id: 'desktopHelper', icon: Monitor, label: 'VoteBeats Bridge' },
    { id: 'eventDefaults', icon: Settings2, label: 'Event Defaults' },
    { id: 'polls', icon: ListChecks, label: 'My Polls' },
    { id: 'security', icon: Lock, label: 'Security' },
    { id: 'notifications', icon: Bell, label: 'Notifications' },
    { id: 'billing', icon: CreditCard, label: 'Billing' },
  ];

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      await updateProfile(user, { displayName: name });
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        displayName: name,
        defaultContactName: defaultContactName || '',
        defaultContactPhone: defaultContactPhone || '',
        defaultContactEmail: defaultContactEmail || '',
      }, { merge: true });
      toast.success('Profile updated successfully');
    } catch {
      toast.error('Failed to update profile');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email) return;

    if (!currentPassword || !newPassword) {
      toast.error('Please fill in both password fields');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      toast.success('Password updated successfully');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update password';
      if (message.includes('wrong-password') || message.includes('invalid-credential')) {
        toast.error('Current password is incorrect');
      } else {
        toast.error(message);
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.email) return;

    setDeleteError('');
    setDeleteLoading(true);

    try {
      // Reauthenticate
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);

      // Events are detached and kept by the server (ITER-154). Stop before removing the account if one fails.
      const eventsQuery = query(collection(db, 'events'), where('djId', '==', user.uid));
      const eventsSnap = await getDocs(eventsQuery);
      for (const eventDoc of eventsSnap.docs) {
        try {
          await deleteEvent(eventDoc.id);
        } catch (eventError: unknown) {
          const reason = eventError instanceof Error ? eventError.message : 'unknown error';
          throw new Error(`Could not remove the event "${eventDoc.data().name ?? eventDoc.id}" (${reason}). Your account was not deleted.`);
        }
      }

      const templatesQuery = query(collection(db, 'eventTemplates'), where('djId', '==', user.uid));
      const templatesSnap = await getDocs(templatesQuery);
      if (templatesSnap.docs.length > 0) {
        const batch5 = writeBatch(db);
        templatesSnap.docs.forEach(d => batch5.delete(d.ref));
        await batch5.commit();
      }

      await deleteDoc(doc(db, 'users', user.uid));
      await deleteUser(user);

      toast.success('Account deleted successfully');
      navigate('/');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete account';
      if (message.includes('wrong-password') || message.includes('invalid-credential')) {
        setDeleteError('Incorrect password');
      } else {
        setDeleteError(message);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage your account preferences</p>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar Tabs */}
        <nav className="md:w-48 flex md:flex-col gap-1">
          {tabs.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'bg-primary text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="flex-1 card p-6">
          {activeTab === 'profile' && (
            <form onSubmit={handleSaveProfile} className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Profile Settings</h2>

              {/* Profile Picture Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Profile Picture
                </label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {profilePic ? (
                      <img
                        src={profilePic}
                        alt="Profile"
                        className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center border-2 border-gray-200 dark:border-gray-700">
                        <User className="w-10 h-10 text-gray-400" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute -bottom-1 -right-1 p-1.5 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-colors"
                      aria-label="Upload profile picture"
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      onChange={handleProfilePicUpload}
                      className="hidden"
                      aria-label="Upload profile picture"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn btn-secondary text-sm"
                      disabled={uploadingPhoto}
                    >
                      {uploadingPhoto ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Photo
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 mt-1">
                      JPEG, PNG, GIF, or WebP • Max 2MB
                    </p>
                  </div>
                </div>
                {uploadError && (
                  <div role="alert" className="mt-2 flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>{uploadError}</span>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="dj-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  DJ Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="dj-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="DJ Awesome"
                    className="input pl-10"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input pl-10"
                    disabled
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>

              {/* Default Contact Info */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Default Contact Info</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Pre-filled when creating new events. You can override per event.
                </p>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="default-contact-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Contact Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        id="default-contact-name"
                        type="text"
                        value={defaultContactName}
                        onChange={(e) => setDefaultContactName(e.target.value)}
                        placeholder="Your name or DJ name"
                        className="input pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="default-contact-phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        id="default-contact-phone"
                        type="tel"
                        value={defaultContactPhone}
                        onChange={(e) => setDefaultContactPhone(e.target.value)}
                        placeholder="(555) 123-4567"
                        className="input pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="default-contact-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Contact Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        id="default-contact-email"
                        type="email"
                        value={defaultContactEmail}
                        onChange={(e) => setDefaultContactEmail(e.target.value)}
                        placeholder="contact@example.com"
                        className="input pl-10"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-primary">
                <Save className="w-5 h-5 mr-2" />
                Save Changes
              </button>
            </form>
          )}

          {activeTab === 'desktopHelper' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">VoteBeats Bridge</h2>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Monitor className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                      Auto Now Playing — VoteBeats Bridge
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Automatically detect what's playing on your computer and update your event's Now Playing for the crowd — no Spotify account required. Works with Spotify, Apple Music, Amazon Music, and any other player.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={BRIDGE_WINDOWS_INSTALLER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary inline-flex items-center gap-2"
                        onClick={(e) => {
                          if (!navigator.userAgent.includes('Windows')) {
                            e.preventDefault();
                            window.open(BRIDGE_LATEST_RELEASE_URL, '_blank');
                          }
                        }}
                      >
                        <Download className="w-4 h-4" />
                        {navigator.userAgent.includes('Windows') ? 'Download for Windows (.exe)' : 'Download VoteBeats Bridge'}
                      </a>
                      {!navigator.userAgent.includes('Windows') && (
                        <a
                          href={BRIDGE_MAC_INSTALLER_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary inline-flex items-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          Download for Mac (.dmg)
                        </a>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                      Install on the computer you DJ from. Sign in with your VoteBeats credentials and select your event — the rest is automatic.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'eventDefaults' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Default Event Settings</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  These settings will be pre-filled when creating new events
                </p>
              </div>

              <div>
                <p className="font-medium text-gray-900 dark:text-white">Default player</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  New events start with this player picked. You can change it on each event until its first request.
                </p>
                <PlayModePicker
                  name="defaultPlayMode"
                  value={eventDefaults.playMode ?? null}
                  onChange={(mode) => setEventDefaults({ ...eventDefaults, playMode: mode })}
                  locked={false}
                />
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Max Queue Size
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={eventDefaults.maxQueueSize || ''}
                    onChange={(e) => setEventDefaults({ ...eventDefaults, maxQueueSize: e.target.value === '' ? 0 : Number(e.target.value) })}
                    className="input w-32"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Player Queue Depth
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    How many songs to keep queued in your music player at once (0 = queue each song when prompted, 1 = only the next song)
                  </p>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={eventDefaults.queueDepth ?? 1}
                    onChange={(e) => setEventDefaults({ ...eventDefaults, queueDepth: clampQueueDepth(Number(e.target.value)) })}
                    className="input w-20"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">Lock songs when queued</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Queue Next also locks the song's position, up to your queue depth. You can change it per event on the Queue page.</p>
                  </div>
                  <button type="button" role="switch" aria-checked={eventDefaults.lockWhenQueued} aria-label="Lock songs when queued" onClick={() => setEventDefaults({ ...eventDefaults, lockWhenQueued: !eventDefaults.lockWhenQueued })} className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${eventDefaults.lockWhenQueued ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.lockWhenQueued ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Queue Mode
                  </label>
                  <select
                    value={eventDefaults.queueMode}
                    onChange={(e) => setEventDefaults({ ...eventDefaults, queueMode: e.target.value as 'standard' | 'ranked' | 'fifo' })}
                    className="input w-48"
                  >
                    <option value="standard">Standard Voting</option>
                    <option value="ranked">Ranked Choice</option>
                    <option value="fifo">First In, First Out</option>
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Event Active by Default</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      New events accept requests immediately
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEventDefaults({ ...eventDefaults, isActive: !eventDefaults.isActive })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      eventDefaults.isActive ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        eventDefaults.isActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Allow Tips by Default</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Enable tipping for new events
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEventDefaults({ ...eventDefaults, allowTips: !eventDefaults.allowTips })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      eventDefaults.allowTips ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        eventDefaults.allowTips ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Auto-Approve by Default</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Automatically approve song requests
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEventDefaults({ ...eventDefaults, autoApprove: !eventDefaults.autoApprove })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      eventDefaults.autoApprove ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        eventDefaults.autoApprove ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Content Filtering */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Content Filtering</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Filter Explicit Content</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Hide explicit songs from search results</p>
                    </div>
                    <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, filterExplicit: !eventDefaults.filterExplicit })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventDefaults.filterExplicit ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.filterExplicit ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Block Explicit Requests</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Prevent attendees from submitting explicit songs</p>
                    </div>
                    <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, blockExplicit: !eventDefaults.blockExplicit })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventDefaults.blockExplicit ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.blockExplicit ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Warn on Explicit</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Show warning badge on explicit requests</p>
                    </div>
                    <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, warnExplicit: !eventDefaults.warnExplicit })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventDefaults.warnExplicit ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.warnExplicit ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Auto-Reject Blocked Songs</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Automatically reject songs on your block list</p>
                    </div>
                    <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, autoRejectBlocked: !eventDefaults.autoRejectBlocked })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventDefaults.autoRejectBlocked ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.autoRejectBlocked ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Allow Attendee Flagging</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Let attendees flag inappropriate songs</p>
                    </div>
                    <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, allowAttendeeFlagging: !eventDefaults.allowAttendeeFlagging })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventDefaults.allowAttendeeFlagging ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.allowAttendeeFlagging ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Request Limits */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Request Limits</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max Requests Per User</label>
                    <input type="number" min={1} max={50} value={eventDefaults.maxRequestsPerUser || ''} onChange={(e) => setEventDefaults({ ...eventDefaults, maxRequestsPerUser: e.target.value === '' ? 0 : Number(e.target.value) })} className="input w-full" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cooldown (minutes)</label>
                    <input type="number" min={0} max={60} value={eventDefaults.cooldownMinutes || ''} onChange={(e) => setEventDefaults({ ...eventDefaults, cooldownMinutes: e.target.value === '' ? 0 : Number(e.target.value) })} className="input w-full" />
                  </div>
                </div>
              </div>

              {/* People's Pick (Super Request) — hidden via feature flag */}
              {!PEOPLES_PICK_HIDDEN && <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">People's Pick</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Enable People's Pick</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Songs auto-queue when enough attendees nominate them</p>
                    </div>
                    <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, superRequestEnabled: !eventDefaults.superRequestEnabled })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventDefaults.superRequestEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.superRequestEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {eventDefaults.superRequestEnabled && (
                    <div className="ml-4 space-y-3 border-l-2 border-primary/30 pl-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Threshold Type</label>
                        <select value={eventDefaults.superRequestThresholdType} onChange={(e) => setEventDefaults({ ...eventDefaults, superRequestThresholdType: e.target.value as 'count' | 'percentage' })} className="input w-40">
                          <option value="count">Fixed Count</option>
                          <option value="percentage">Percentage of Attendees</option>
                        </select>
                      </div>
                      {eventDefaults.superRequestThresholdType === 'count' ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nomination Count</label>
                          <input type="number" min={2} max={50} value={eventDefaults.superRequestThresholdCount || ''} onChange={(e) => setEventDefaults({ ...eventDefaults, superRequestThresholdCount: e.target.value === '' ? 0 : Number(e.target.value) })} className="input w-24" />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nomination Percentage (%)</label>
                          <input type="number" min={1} max={100} value={eventDefaults.superRequestThresholdPercent || ''} onChange={(e) => setEventDefaults({ ...eventDefaults, superRequestThresholdPercent: e.target.value === '' ? 0 : Number(e.target.value) })} className="input w-24" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>}

              {/* Vote Rate Limiting (Doom Voting) */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Vote Rate Limiting</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Enable Vote Rate Limiting</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Limit how many votes attendees can cast in a time window</p>
                    </div>
                    <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, doomVotingEnabled: !eventDefaults.doomVotingEnabled })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventDefaults.doomVotingEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.doomVotingEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {eventDefaults.doomVotingEnabled && (
                    <div className="ml-4 space-y-3 border-l-2 border-primary/30 pl-4">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max Votes</label>
                          <input type="number" min={1} max={100} value={eventDefaults.doomVotingMaxVotes || ''} onChange={(e) => setEventDefaults({ ...eventDefaults, doomVotingMaxVotes: e.target.value === '' ? 0 : Number(e.target.value) })} className="input w-full" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Active (min)</label>
                          <input type="number" min={1} max={30} value={eventDefaults.doomVotingActiveMinutes || ''} onChange={(e) => setEventDefaults({ ...eventDefaults, doomVotingActiveMinutes: e.target.value === '' ? 0 : Number(e.target.value) })} className="input w-full" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rest (min)</label>
                          <input type="number" min={1} max={60} value={eventDefaults.doomVotingRestMinutes || ''} onChange={(e) => setEventDefaults({ ...eventDefaults, doomVotingRestMinutes: e.target.value === '' ? 0 : Number(e.target.value) })} className="input w-full" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Downvoting */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">Enable Downvoting</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Allow attendees to downvote songs</p>
                  </div>
                  <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, downvotingEnabled: !eventDefaults.downvotingEnabled })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventDefaults.downvotingEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.downvotingEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                {eventDefaults.downvotingEnabled && (
                  <div className="mt-3 ml-4 pl-3 border-l-2 border-gray-200 dark:border-gray-700 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">A majority of downvotes votes a song out</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Out above the line, back 10 points under it. Starts at {MIN_PARTICIPANTS} people taking part.</p>
                      </div>
                      <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, voteOutEnabled: !eventDefaults.voteOutEnabled })} className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${eventDefaults.voteOutEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.voteOutEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    {eventDefaults.voteOutEnabled && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Vote out above</label>
                        <select value={eventDefaults.voteOutThresholdPercent} onChange={(e) => setEventDefaults({ ...eventDefaults, voteOutThresholdPercent: Number(e.target.value) })} className="input w-full">
                          {VOTE_OUT_THRESHOLD_OPTIONS.map(option => <option key={option} value={option}>{option}%</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Crowd favorites (ITER-075) */}
              {eventDefaults.queueMode !== 'fifo' && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Jump a crowd favorite to the top</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Needs {MIN_PARTICIPANTS} people taking part. Favorites play right after locked songs, ahead of rankings and your manual order.</p>
                    </div>
                    <button type="button" onClick={() => setEventDefaults({ ...eventDefaults, upvoteOverrideEnabled: !eventDefaults.upvoteOverrideEnabled })} className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${eventDefaults.upvoteOverrideEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventDefaults.upvoteOverrideEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {eventDefaults.upvoteOverrideEnabled && (
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">When at least … of everyone taking part upvotes it</label>
                      <select value={eventDefaults.upvoteOverridePercent} onChange={(e) => setEventDefaults({ ...eventDefaults, upvoteOverridePercent: Number(e.target.value) })} className="input w-full">
                        {UPVOTE_OVERRIDE_OPTIONS.map(option => <option key={option} value={option}>{option}%</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Queue Visibility */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Queue Visibility</h3>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pre-Event Queue View</label>
                    <select value={eventDefaults.preEventQueueView} onChange={(e) => setEventDefaults({ ...eventDefaults, preEventQueueView: e.target.value as 'full' | 'own_only' | 'blind' })} className="input w-48">
                      <option value="full">Full Queue</option>
                      <option value="own_only">Own Requests Only</option>
                      <option value="blind">Hidden (Blind)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">During Event Queue View</label>
                    <select value={eventDefaults.duringEventQueueView} onChange={(e) => setEventDefaults({ ...eventDefaults, duringEventQueueView: e.target.value as 'now_and_upcoming' | 'now_only' | 'hide' })} className="input w-48">
                      <option value="now_and_upcoming">Now Playing + Upcoming</option>
                      <option value="now_only">Now Playing Only</option>
                      <option value="hide">Hidden</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Post-Close Queue View</label>
                    <select value={eventDefaults.postCloseQueueView} onChange={(e) => setEventDefaults({ ...eventDefaults, postCloseQueueView: e.target.value as 'show' | 'hide' })} className="input w-48">
                      <option value="show">Show Queue</option>
                      <option value="hide">Hide Queue</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Crossfade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Crossfade (seconds)
                </label>
                <input
                  type="number"
                  min={0}
                  max={12}
                  value={eventDefaults.crossfadeSeconds}
                  onChange={(e) => setEventDefaults({ ...eventDefaults, crossfadeSeconds: Math.min(12, Math.max(0, Number(e.target.value))) })}
                  className="input w-24"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Seconds of overlap between songs. Also sets the fade in and out of local files. 0 = straight cut.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Auto-Match from Library</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    When a requested song matches your saved library, auto-queue it with the saved platform link.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEventDefaults(prev => ({ ...prev, autoMatchFromLibrary: !prev.autoMatchFromLibrary }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    eventDefaults.autoMatchFromLibrary ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      eventDefaults.autoMatchFromLibrary ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div>
                <label htmlFor="link-confidence-threshold" className="block font-medium text-gray-900 dark:text-white mb-1">
                  Saved Link Warning
                </label>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 flex-wrap">
                  <span>Warn me when a saved link has fewer than</span>
                  <input
                    id="link-confidence-threshold"
                    type="number"
                    min={0}
                    max={20}
                    step={1}
                    value={eventDefaults.linkConfidenceThreshold}
                    onChange={(e) => setEventDefaults({
                      ...eventDefaults,
                      linkConfidenceThreshold: Math.min(20, Math.max(0, Math.round(Number(e.target.value) || 0))),
                    })}
                    className="input w-20"
                  />
                  <span>clean uses</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  A clean use is a song that played with that link and no wrong-song report. 0 turns the warning off.
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleResetEventDefaults}
                  className="btn btn-secondary"
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  Reset to System Defaults
                </button>
                <button
                  type="button"
                  onClick={handleSaveEventDefaults}
                  className="btn btn-primary"
                >
                  <Save className="w-5 h-5 mr-2" />
                  Save Defaults
                </button>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <form onSubmit={handleChangePassword} className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Security Settings</h2>

              {/* Session Timeout Setting */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div className="flex-1">
                    <label htmlFor="session-timeout" className="block font-medium text-gray-900 dark:text-white mb-1">
                      Session Timeout
                    </label>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Automatically log out after a period of inactivity
                    </p>
                    <select
                      id="session-timeout"
                      value={sessionTimeoutMinutes}
                      onChange={(e) => {
                        setSessionTimeoutMinutes(Number(e.target.value));
                        toast.success(`Session timeout set to ${SESSION_TIMEOUT_OPTIONS.find(o => o.value === Number(e.target.value))?.label}`);
                      }}
                      className="input w-48"
                    >
                      {SESSION_TIMEOUT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <hr className="border-gray-200 dark:border-gray-700" />

              <h3 className="text-md font-medium text-gray-900 dark:text-white">Change Password</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                />
              </div>

              <button type="submit" className="btn btn-primary">
                <Lock className="w-5 h-5 mr-2" />
                Update Password
              </button>

              <hr className="border-gray-200 dark:border-gray-700" />

              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Dismissed Prompts</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Reset all dismissed prompts (duration verification, countdown warnings, etc.) so they appear again.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    // Clear all dismissed prompts from sessionStorage
                    const keysToRemove: string[] = [];
                    for (let i = 0; i < sessionStorage.length; i++) {
                      const key = sessionStorage.key(i);
                      if (key && (key.includes('duration_verify') || key.includes('votebeats_warn'))) {
                        keysToRemove.push(key);
                      }
                    }
                    keysToRemove.forEach(k => sessionStorage.removeItem(k));
                    toast.success('All dismissed prompts have been reset');
                  }}
                  className="btn btn-secondary"
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  Reset All Dismissed Prompts
                </button>
              </div>

              <hr className="border-gray-200 dark:border-gray-700" />

              <div>
                <h3 className="text-lg font-medium text-danger mb-2">Danger Zone</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(true);
                    setDeletePassword('');
                    setDeleteError('');
                  }}
                  className="btn text-danger border-danger hover:bg-danger hover:text-white"
                >
                  <Trash2 className="w-5 h-5 mr-2" />
                  Delete Account
                </button>
              </div>
            </form>
          )}

          {activeTab === 'polls' && user && <PollLibraryPanel djId={user.uid} />}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Settings</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Email Notifications</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Receive updates about your events via email
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleEmailNotifications}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      emailNotifications ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        emailNotifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Push Notifications</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Receive real-time notifications in browser
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleTogglePushNotifications}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      pushNotifications ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        pushNotifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Billing Settings</h2>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 text-center">
                <CreditCard className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Free Plan
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  You're currently on the free plan. Upgrade to unlock more features.
                </p>
                <button className="btn btn-primary">
                  Upgrade to Pro
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-danger" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Account
                </h3>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              This action cannot be undone. Your account and saved settings will be deleted, and your events will be removed from your account. VoteBeats keeps event history as described in the Privacy Policy.
            </p>

            <div className="space-y-4">
              {deleteError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-danger">{deleteError}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Enter your password to confirm
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your password"
                  className="input"
                  disabled={deleteLoading}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="btn btn-secondary flex-1"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  className="btn bg-danger text-white hover:bg-red-600 flex-1"
                  disabled={deleteLoading || !deletePassword}
                >
                  {deleteLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5 mr-2" />
                      Delete Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
